import { memo, useId, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsMetricRow, {
  InsightsMetricUniformProvider,
} from "./InsightsMetricRow";
import { insightsUniformRowsReserveDeltaLane } from "./InsightsDeltaBadge";
import InsightsSectionHeading from "./InsightsSectionHeading";
import InsightsProgramAlertRow from "./InsightsProgramAlertRow";

/**
 * @param {Record<string, unknown>} params — alert params; `groupKey` resolves via `panel.muscles.*`.
 * @returns {Record<string, unknown>}
 */
function resolveVolumeCoverageAlertParams(t, params) {
  if (!params || typeof params !== "object") {
    return {};
  }
  const next = { ...params };
  if (typeof next.groupKey === "string") {
    next.group = t(`programs.programInsights.panel.muscles.${next.groupKey}`);
    delete next.groupKey;
  }
  return next;
}

/**
 * Hypertrophy-style bottom block: volume & coverage summary (metrics + alerts from week series).
 *
 * @param {object} props
 * @param {{ metrics: object[], alerts: object[] }} props.volumeCoverage
 * @param {number} props.syncRowIndex — cross-column row sync (same index as powerlifting per-lift block).
 */
function InsightsVolumeCoverageSection({ volumeCoverage, syncRowIndex }) {
  const { t } = useTranslation("athlete-portal");
  const sectionTitleId = useId();

  const infoLabels = useMemo(
    () => ({
      workoutsPerWeek: t(
        "programs.programInsights.panel.volumeCoverage.metricInfo.workoutsPerWeek"
      ),
      avgSetsPerMuscle: t(
        "programs.programInsights.panel.volumeCoverage.metricInfo.avgSetsPerMuscle"
      ),
      amrapSets: t(
        "programs.programInsights.panel.volumeCoverage.metricInfo.amrapSets"
      ),
    }),
    [t]
  );

  return (
    <section
      className="ProgramInsights-section ProgramInsights-section--volumeCoverage"
      aria-labelledby={sectionTitleId}
      data-insights-sync-row={syncRowIndex}
    >
      <InsightsSectionHeading
        titleId={sectionTitleId}
        title={t("programs.programInsights.panel.volumeCoverage.sectionTitle")}
        infoAriaLabel={t(
          "programs.programInsights.panel.volumeCoverage.sectionInfo"
        )}
      />
      <InsightsMetricUniformProvider
        reserveDeltaSlot={insightsUniformRowsReserveDeltaLane(
          volumeCoverage.metrics
        )}
      >
        <div className="ProgramInsights-sectionBody">
          {volumeCoverage.metrics.map((m) => (
            <InsightsMetricRow
              key={m.id}
              label={t(
                `programs.programInsights.panel.volumeCoverage.metrics.${m.id}`
              )}
              valueDisplay={m.valueDisplay}
              delta={m.delta}
              infoAriaLabel={infoLabels[m.id]}
              uniformLayout
              showNeutralDeltaBadge
            />
          ))}
        </div>
      </InsightsMetricUniformProvider>
      <p className="ProgramInsights-volumeCoverageNote">
        {t("programs.programInsights.panel.volumeCoverage.coverageNote")}
      </p>
      <ul
        className="ProgramInsights-volumeCoverageAlerts"
        aria-label={t(
          "programs.programInsights.panel.volumeCoverage.alertsListAria"
        )}
      >
        {volumeCoverage.alerts.map((alert, index) => {
          const body = t(
            `programs.programInsights.panel.volumeCoverage.${alert.messageKey}`,
            resolveVolumeCoverageAlertParams(t, alert.params)
          );

          return (
            <InsightsProgramAlertRow
              key={`${alert.variant}-${alert.messageKey}-${String(alert.params?.groupKey ?? "")}-${String(index)}`}
              as="li"
              variant={alert.variant}
            >
              {body}
            </InsightsProgramAlertRow>
          );
        })}
      </ul>
    </section>
  );
}

InsightsVolumeCoverageSection.propTypes = {
  volumeCoverage: PropTypes.shape({
    metrics: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        valueDisplay: PropTypes.string.isRequired,
        delta: PropTypes.object,
      })
    ).isRequired,
    alerts: PropTypes.arrayOf(
      PropTypes.shape({
        variant: PropTypes.oneOf(["info", "warning", "success"]).isRequired,
        messageKey: PropTypes.string.isRequired,
        params: PropTypes.object,
      })
    ).isRequired,
  }).isRequired,
  syncRowIndex: PropTypes.number.isRequired,
};

export default memo(InsightsVolumeCoverageSection);
